import { Alert, Box, Breadcrumbs, Grid2 as Grid, Stack, Typography } from "@mui/material"
import { folderState, historyInterface } from "."
import { TextField } from "../textField"
import React from "react"
import { useAppDispatch } from "../../redux"
import { useCreateFolderMutation, useUpdateFolderMutation } from "../../redux/services"
import { useSnackbar } from "notistack"
import { LoadingButton } from "@mui/lab"
import { closeDialog } from "../../redux/slices/dialog"
import { NavigateNext } from "@mui/icons-material"
import { CapitalizeString } from "../../utils"

interface addEditFolderState {
    object_id?: string,
    object_type?: string,
    parentFolder?: folderState,
    folder?: folderState,
    isEdit: boolean,
    history?: historyInterface[],
    refetch: (updatedData: Partial<folderState>) => void,
    variant?: 'folder' | 'template'
}

export const AddEditFolder: React.FC<addEditFolderState> = ({
    parentFolder, folder, object_id, object_type, isEdit, refetch, history = [], variant = 'folder'
}) => {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [createFolder, createFolderData] = useCreateFolderMutation();
    const [updateFolder, updateFolderData] = useUpdateFolderMutation();

    const [state, setState] = React.useState<{ folder_name: string, description?: string }>({ folder_name: folder?.folder_name ?? "", description: folder?.description ?? "" });
    const [message, setMessage] = React.useState<string>('');

    const capitalizedVariant = CapitalizeString(variant);
    const normalVariant = variant;

    const addEditFolder = () => {
        setMessage('');
        if (state.folder_name) {
            if (!isEdit) {
                createFolder({
                    ...state,
                    parent_object_id: parentFolder && parentFolder._id !== 'root' ? parentFolder._id : object_id,
                    parent_object_type: parentFolder && parentFolder._id !== 'root' ? 'folder' : object_type,
                }).unwrap().then((res) => {
                    if (res.title) {
                        enqueueSnackbar(res.title, { variant: 'error' });
                    } else {
                        refetch(state);
                        dispatch(closeDialog());
                        enqueueSnackbar(`${capitalizedVariant} Created`, { variant: "success" });
                    }
                }).catch(err => {
                    console.log('err:', err);
                    enqueueSnackbar(err.data?.title ?? `Something went wrong, Unable to create ${normalVariant}.`, { variant: 'error' });
                });
            } else {
                updateFolder({
                    id: `${folder?._id}`,
                    payload: { ...folder, ...state }
                }).unwrap().then((res) => {
                    if (res.title) {
                        enqueueSnackbar(res.title, { variant: 'error' });
                    } else {
                        refetch(state);
                        dispatch(closeDialog());
                        enqueueSnackbar(`${capitalizedVariant} Updated`, { variant: "success" });
                    }
                }).catch(err => {
                    console.log('err:', err);
                    enqueueSnackbar(err.data?.title ?? `Something went wrong, Unable to update ${normalVariant}.`, { variant: 'error' });
                })
            }
        } else {
            setMessage(`Please enter ${normalVariant} name`)
        }
    };

    return <Box>

        {/* Breadscrum */}
        <Breadcrumbs sx={{ mb: 3 }} separator={<NavigateNext fontSize="small" />}>
            {history.map((his, index) => {
                return <Typography key={index} variant="body1">
                    {his.label}
                </Typography>
            })}

            {parentFolder && <Typography variant="body1">
                {parentFolder?.folder_name ?? ""}
            </Typography>}

            <Typography variant="body1" color="primary">
                {isEdit ? `Edit` : `New ${capitalizedVariant}`}
            </Typography>
        </Breadcrumbs>

        <Grid container gap={2}>
            <Grid size={{ xs: 12 }}>
                <TextField
                    size="small"
                    required
                    label={`${capitalizedVariant} Name`}
                    fullWidth
                    variant="outlined"
                    value={state.folder_name}
                    onChange={(e) => setState({ ...state, folder_name: e.target.value })}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <TextField
                    size="small"
                    multiline
                    rows={5}
                    label={"Description"}
                    fullWidth
                    variant="outlined"
                    value={state.description}
                    onChange={(e) => setState({ ...state, description: e.target.value })}
                />
            </Grid>

            {message && <Grid size={{ xs: 12 }}>
                <Alert severity="warning">{message}</Alert>
            </Grid>}

            {(createFolderData.isError || updateFolderData.isError) && <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                    {!isEdit && <>{(createFolderData.error as any)?.data?.title ?? `Something went wrong, Unable to create ${normalVariant}`}</>}
                    {isEdit && <>{(updateFolderData.error as any)?.data?.title ?? `Something went wrong, Unable to update ${normalVariant}`}</>}
                </Alert>
            </Grid>}

            <Grid size={{ xs: 12 }}>
                <Stack direction={"row"} sx={{ width: "100%", justifyContent: "flex-end" }} gap={1}>
                    <LoadingButton
                        disabled={createFolderData.isLoading || updateFolderData.isLoading}
                        variant="outlined"
                        onClick={() => dispatch(closeDialog())}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        loading={createFolderData.isLoading || updateFolderData.isLoading}
                        variant="contained"
                        onClick={() => addEditFolder()}
                    >
                        {isEdit ? `Update ${capitalizedVariant}` : `Create ${capitalizedVariant}`}
                    </LoadingButton>
                </Stack>
            </Grid>
        </Grid>
    </Box >
}